import {
	getValuationIdFromPath,
	getSelectedEntityFromPath,
} from '@dealstack/frontend/app/pages/Valuations/hooks/utils';
import {
	ValuationLocation,
	ValuationPathPart,
	VALUATIONS_ROUTES,
} from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

type ValuationPaths = keyof typeof VALUATIONS_ROUTES;
type RoutePathArgs<T extends ValuationPaths> = Parameters<(typeof VALUATIONS_ROUTES)[T]>[0];

export const useValuationNavigation = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { dealId } = useParams<{
		dealId?: string;
	}>();

	// Extract valuation ID from path
	const pathValuationId = getValuationIdFromPath(pathname);

	// Extract person and group IDs from query parameters
	const { personId: pathPersonId, mipId: pathMipId } = getSelectedEntityFromPath(
		pathname,
		search
	);

	// Determine current location
	const pathParts = pathname.split('/');
	const lastPart = pathParts[pathParts.length - 1];
	let currentLocation: ValuationLocation | undefined;
	switch (lastPart) {
		case ValuationPathPart.VALUATIONS:
			currentLocation = ValuationLocation.ROOT;
			break;
		case ValuationPathPart.SUMMARY:
			currentLocation = ValuationLocation.SUMMARY;
			break;
		case ValuationPathPart.SOURCES_AND_USES:
			currentLocation = ValuationLocation.SOURCES_AND_USES;
			break;
		case ValuationPathPart.BREAKPOINT_SUMMARY:
			currentLocation = ValuationLocation.BREAKPOINT_SUMMARY;
			break;
		default:
			if (pathParts.includes(ValuationPathPart.VALUATIONS) && pathParts.length > 1) {
				currentLocation = ValuationLocation.SPECIFIC;
			} else {
				currentLocation = undefined;
			}
	}

	const navigateTo = useCallback(
		<T extends ValuationPaths>(path: T, args: RoutePathArgs<T>) => {
			const routeFn = VALUATIONS_ROUTES[path] as (...args: unknown[]) => string;
			const url = routeFn(args);
			navigate(url);
		},
		[navigate]
	);

	const props = useMemo(
		() => ({
			dealId,
			pathValuationId,
			pathPersonId,
			pathMipId,
			navigateTo,
			currentLocation,
		}),
		[dealId, pathValuationId, pathPersonId, pathMipId, navigateTo, currentLocation]
	);

	return props;
};
